/**
 * AdminReportsAndExports
 * ----------------------
 * 100% read-only admin widget (replaces the previous "Admin Maintenance
 * Tools" dropdown). Provides quick access to operational reports and
 * exports for offline/preview-env analysis.
 *
 * Two sections:
 *   1. Analysis Scripts — pre-defined dry-run scripts that produce
 *      textual diagnostic output (Fingerprint Dedup Preview, DB Stats,
 *      Year Sync Preview). Same /api/admin/tools infrastructure as
 *      before but with dangerous commands removed from the whitelist.
 *
 *   2. Reports & Exports — JSON-backed reports (Top Songs Without Year,
 *      Near-Duplicate Candidates, Jingle Candidates, Station Health)
 *      plus a full canonical DB download. Each report renders an
 *      inline table and offers a CSV download.
 *
 * No destructive actions are exposed here. If an emergency rollback or
 * forced execute is required, run the corresponding script from the
 * backend container terminal (see /app/backend/scripts/*.py).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  RefreshCw, Download, FileText, AlertCircle, Loader2, Shield,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ── CSV helper ────────────────────────────────────────────────────────
function downloadCSV(filename, rows) {
  if (!rows || !rows.length) return;
  const headers = Object.keys(rows[0]);
  const escape = (v) => {
    if (v == null) return '';
    const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const body = [headers.join(','), ...rows.map((r) => headers.map((h) => escape(r[h])).join(','))].join('\n');
  const blob = new Blob(['\uFEFF' + body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ════════════════════════════════════════════════════════════════════════
// ReportRunner — single card with execute / download / preview
// ════════════════════════════════════════════════════════════════════════
const ReportRunner = ({ report, token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // v2.9.147 (2026-06-08) — copy-to-clipboard feedback flag.
  const [jsonCopied, setJsonCopied] = useState(false);

  const copyJson = async () => {
    if (!data) return;
    const payload = JSON.stringify(data, null, 2);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(payload);
      } else {
        // Fallback for non-secure contexts
        const ta = document.createElement('textarea');
        ta.value = payload;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setJsonCopied(true);
      setTimeout(() => setJsonCopied(false), 1800);
    } catch (_) {
      // noop — user can still copy from the <pre> by hand
    }
  };

  const runReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}${report.endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  // For download-only reports (canonical-export), open a new tab with the auth token
  const runDownload = () => {
    // Use fetch with Authorization header — then trigger download from blob
    setLoading(true);
    setError(null);
    fetch(`${API}${report.endpoint}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const cd = r.headers.get('Content-Disposition') || '';
        const match = cd.match(/filename="?([^"]+)"?/);
        const name = match ? match[1] : `export_${Date.now()}.json`;
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  // Extract tabular rows from the response (most endpoints expose `rows`
  // or `stations`; others expose a flat list)
  const rows =
    data?.rows ||
    data?.stations ||
    (Array.isArray(data) ? data : null) ||
    [];

  const summary = data?.summary;

  return (
    <div
      className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 p-4"
      data-testid={`report-card-${report.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1">
          <h4 className="font-semibold text-gray-800 dark:text-gray-100 mb-0.5">
            {report.label}
          </h4>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            {report.description}
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {report.is_download ? (
            <button
              onClick={runDownload}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium"
              data-testid={`report-download-${report.id}`}
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              Κατέβασμα
            </button>
          ) : (
            <button
              onClick={runReport}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-medium"
              data-testid={`report-run-${report.id}`}
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
              Εκτέλεση
            </button>
          )}
          {!report.is_download && rows.length > 0 && (
            <button
              onClick={() => downloadCSV(`${report.id}_${Date.now()}.csv`, rows)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
              data-testid={`report-csv-${report.id}`}
            >
              <Download className="w-3.5 h-3.5" />
              CSV
            </button>
          )}
          {/* v2.9.147 — Copy JSON button. Available for any
              non-download report once data is loaded. Especially
              useful for the "Memory & Recovery Snapshot" entry
              whose payload is raw diagnostic JSON the operator
              needs to paste into a support ticket. */}
          {!report.is_download && data && (
            <button
              onClick={copyJson}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md font-medium transition-colors ${
                jsonCopied
                  ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/40'
                  : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600'
              }`}
              data-testid={`report-copy-json-${report.id}`}
              title="Αντιγραφή του πλήρους JSON στο clipboard (για support tickets)"
            >
              {jsonCopied ? '✓ Αντιγράφηκε' : '📋 Copy JSON'}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-2 p-2 text-xs rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800">
          Σφάλμα: {error}
        </div>
      )}

      {summary && !report.is_download && (
        <div className="mt-3 text-xs text-gray-700 dark:text-gray-300 grid grid-cols-2 md:grid-cols-4 gap-2">
          {Object.entries(summary).map(([k, v]) => (
            <div key={k} className="rounded bg-gray-50 dark:bg-gray-700/40 px-2 py-1">
              <div className="text-[10px] uppercase tracking-wide opacity-60">{k}</div>
              <div className="font-mono text-sm tabular-nums">{String(v)}</div>
            </div>
          ))}
        </div>
      )}

      {data && !report.is_download && (
        <div className="mt-3 text-[11px] text-gray-500 dark:text-gray-400">
          {rows.length > 0
            ? `${rows.length} εγγραφές · Δημιουργήθηκε: ${data.generated_at ? new Date(data.generated_at).toLocaleString('el-GR') : '—'}`
            : 'Καμία εγγραφή'}
        </div>
      )}

      {rows.length > 0 && !report.is_download && (
        <div className="mt-2 overflow-x-auto max-h-80 border border-gray-200 dark:border-gray-700 rounded-md">
          <table className="w-full text-xs" data-testid={`report-table-${report.id}`}>
            <thead className="bg-gray-100 dark:bg-gray-700 sticky top-0">
              <tr>
                {Object.keys(rows[0]).slice(0, 8).map((k) => (
                  <th key={k} className="text-left font-medium text-gray-700 dark:text-gray-200 px-2 py-1.5 whitespace-nowrap">
                    {k}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {rows.slice(0, 100).map((r, i) => (
                <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                  {Object.entries(r).slice(0, 8).map(([k, v]) => (
                    <td key={k} className="px-2 py-1 text-gray-700 dark:text-gray-300 font-mono tabular-nums max-w-[240px] truncate">
                      {v == null ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > 100 && (
            <div className="text-[11px] text-gray-500 dark:text-gray-400 px-2 py-1 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60">
              Εμφανίζονται τα πρώτα 100 από {rows.length}. Κατέβασε CSV για πλήρη λίστα.
            </div>
          )}
        </div>
      )}

      {/* v2.9.147 — Raw-JSON fallback view for endpoints whose
          response is NOT a tabular {rows: [...]} structure (e.g.
          memory-breakdown). Always available when data is loaded;
          collapsible by max-height so it doesn't dominate the card. */}
      {data && !report.is_download && rows.length === 0 && (
        <div className="mt-2 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-900/40">
          <div className="flex items-center justify-between px-2 py-1 text-[11px] text-gray-600 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
            <span>Raw JSON response</span>
            <span className="font-mono tabular-nums">
              {JSON.stringify(data).length.toLocaleString()} chars
            </span>
          </div>
          <pre
            className="max-h-96 overflow-auto p-2 text-[11px] font-mono leading-snug text-gray-700 dark:text-gray-300"
            data-testid={`report-json-${report.id}`}
          >
{JSON.stringify(data, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};


// ════════════════════════════════════════════════════════════════════════
// Top-level widget — renders the banner + read-only report cards
// ════════════════════════════════════════════════════════════════════════
const AdminReportsAndExports = ({ token }) => {
  const [reports, setReports] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/api/admin/reports/catalog`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setReports(res.data.reports || []))
      .catch((err) => setError(err.response?.data?.detail || err.message));
  }, [token]);

  return (
    <div className="space-y-4" data-testid="admin-reports-widget">
      {/* Header banner — clearly states purpose */}
      <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-emerald-600 dark:text-emerald-300 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-emerald-900 dark:text-emerald-100">
              📊 Admin Reports & Exports
            </h3>
            <p className="text-sm text-emerald-800 dark:text-emerald-200 mt-0.5">
              <strong>Read-only εργαλείο αναφορών</strong> — δεν κάνει καμία
              αλλαγή στη βάση δεδομένων. Όλες οι αναφορές εξάγονται σε CSV ή
              JSON για ανάλυση σε preview environment.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 p-3 flex items-center gap-2 text-sm">
          <AlertCircle className="w-4 h-4" />
          Σφάλμα: {error}
        </div>
      )}

      {/* JSON-backed reports — read-only */}
      <div className="space-y-3">
        {reports.map((r) => (
          <ReportRunner key={r.id} report={r} token={token} />
        ))}
      </div>

      <p className="text-[11px] text-gray-500 dark:text-gray-400 italic text-center">
        Οι καταστρεπτικές ενέργειες δεν εκτίθενται από το UI και διατίθενται
        ως python scripts στο{' '}
        <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">/app/backend/scripts/</code> για
        emergency χρήση από το terminal.
      </p>
    </div>
  );
};

// Back-compat: old imports still reference AdminMaintenanceTools
export { AdminReportsAndExports };
export default AdminReportsAndExports;
